import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Barcode, PackagePlus, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { fmtDateTime } from "@/lib/format";
import { SerialScanButton } from "@/components/SerialScanner";
import { WarehouseSelect } from "@/components/warehouse/StockTab";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const F = ({ label, children, className = "" }) => <div className={className}><Label className="micro-label">{label}</Label><div className="mt-1.5">{children}</div></div>;
const match = (q, i) => `${i.code} ${i.name} ${i.barcode || ""} ${(i.alt_codes || []).join(" ")}`.toLowerCase().includes(q.toLowerCase());
const sameSerial = (a, b) => (a || "").trim().toUpperCase() === (b || "").trim().toUpperCase();
const whereLabel = (s) => s.warehouse_name || s.warehouse?.name || s.status;

const useSerialSearch = (q, enabled) => {
  const [hits, setHits] = useState([]);
  useEffect(() => {
    const c = q.trim();
    if (!enabled || c.length < 5) { setHits([]); return undefined; }
    const t = setTimeout(() => api.get("/warehouse/serials", { params: { q: c } }).then((r) => setHits(r.data.slice(0, 10))).catch(() => setHits([])), 250);
    return () => clearTimeout(t);
  }, [q, enabled]);
  return hits;
};

export const ScanInput = ({ placeholder, onCode, testId, title }) => {
  const [v, setV] = useState("");
  const submit = () => { if (v.trim()) { onCode(v.trim()); setV(""); } };
  return (
    <div className="flex gap-2">
      <div className="relative flex-1">
        <Barcode className="absolute left-2.5 top-3 h-5 w-5 opacity-50" />
        <Input className="h-11 bg-white pl-10 font-mono" placeholder={placeholder} value={v} onChange={(e) => setV(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }} data-testid={testId} />
      </div>
      <SerialScanButton onDetected={onCode} testId={`${testId}-camera`} title={title} />
    </div>
  );
};

export const ItemPicker = ({ items, onPick, testId, serials = false, placeholder = "Cerca articolo per codice, descrizione o codice a barre…" }) => {
  const [q, setQ] = useState("");
  const shown = q.length >= 2 ? items.filter((i) => match(q, i)).slice(0, 30) : [];
  const hits = useSerialSearch(q, serials);
  const exact = (code) => items.find((i) => [i.barcode, i.code, ...(i.alt_codes || [])].some((c) => c && String(c).toUpperCase() === code.trim().toUpperCase()));
  const pickSerial = (s) => {
    const it = items.find((i) => i.id === s.item_id);
    if (!it) return toast.error(`Seriale ${s.serial}: articolo non attivo`);
    onPick(it, s);
    setQ("");
    toast.success(`S/N ${s.serial} · ${it.code} (${whereLabel(s)})`);
  };
  const byCode = async (code) => {
    const it = exact(code);
    if (it) { onPick(it); setQ(""); return; }
    const c = code.trim();
    if (serials && c) {
      const { data } = await api.get("/warehouse/serials", { params: { q: c } }).catch(() => ({ data: [] }));
      const one = data.filter((s) => sameSerial(s.serial, c));
      if (one.length === 1) return pickSerial(one[0]);
    }
    if (shown.length === 1) { onPick(shown[0]); setQ(""); return; }
    setQ(code);
    toast.error(`Nessun articolo${serials ? " o seriale" : ""} con codice ${code}`);
  };
  return (
    <div className="relative">
      <div className="flex gap-2">
        <Input className="h-11 bg-white" placeholder={placeholder} value={q} onChange={(e) => setQ(e.target.value)} data-testid={testId}
          onKeyDown={(e) => { if (e.key === "Enter" && q.trim()) { e.preventDefault(); byCode(q); } }} />
        <SerialScanButton onDetected={byCode} testId={`${testId}-scan`} title="Scansiona codice a barre articolo" />
      </div>
      {(shown.length > 0 || hits.length > 0) && (
        <div className="absolute z-30 mt-1 max-h-72 w-full overflow-y-auto border bg-white shadow-lg" data-testid={`${testId}-options`}>
          {hits.length > 0 && <div className="micro-label border-b px-3 py-1.5" data-testid={`${testId}-serials`}>Seriali</div>}
          {hits.map((s) => (
            <button key={s.id} type="button" className="flex w-full justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-accent" onClick={() => pickSerial(s)} data-testid={`${testId}-serial-${s.serial}`}>
              <span><span className="font-mono text-xs font-semibold">S/N {s.serial}</span> · {s.item_code || items.find((i) => i.id === s.item_id)?.code}</span><span className="text-xs text-muted-foreground">{whereLabel(s)}</span>
            </button>
          ))}
          {hits.length > 0 && shown.length > 0 && <div className="micro-label border-y px-3 py-1.5">Articoli</div>}
          {shown.map((i) => (
            <button key={i.id} type="button" className="flex w-full justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-accent" onClick={() => { onPick(i); setQ(""); }} data-testid={`${testId}-opt-${i.code}`}>
              <span><span className="font-mono text-xs font-semibold">{i.code}</span> · {i.name}</span><span className="text-xs text-muted-foreground">{i.unit}</span>
            </button>
          ))}
        </div>
      )}
      {q.length >= 2 && !shown.length && !hits.length && <p className="mt-1 text-xs text-muted-foreground" data-testid={`${testId}-empty`}>Nessun articolo{serials ? " o seriale" : ""} trovato.</p>}
    </div>
  );
};

const AssociateDialog = ({ code, items, onClose, onDone }) => {
  const [item, setItem] = useState(null);
  const save = async () => {
    try { await api.post(`/warehouse/items/${item.id}/barcode`, { barcode: code }); toast.success(`Codice a barre associato a ${item.code}`); onDone({ ...item, barcode: code }); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!code} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="barcode-associate-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Codice a barre sconosciuto</DialogTitle></DialogHeader>
        <p className="text-sm">Il codice <b className="font-mono" data-testid="barcode-associate-code">{code}</b> non è associato a nessun articolo. Scegli l'articolo: verrà memorizzato per le prossime letture.</p>
        <ItemPicker items={items} onPick={setItem} testId="barcode-associate-search" />
        {item && <p className="text-sm" data-testid="barcode-associate-selected">Articolo: <b className="font-mono">{item.code}</b> · {item.name}</p>}
        <DialogFooter><Button disabled={!item} onClick={save} data-testid="barcode-associate-save">Associa e aggiungi</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ConsumableLines = ({ items, lines, setLines, setItems }) => {
  const [unknown, setUnknown] = useState("");
  const add = (item, n = 1) => setLines((ls) => (ls.some((l) => l.item.id === item.id) ? ls.map((l) => (l.item.id === item.id ? { ...l, qty: Number(l.qty || 0) + n } : l)) : [...ls, { item, qty: n }]));
  const onCode = (code) => {
    const item = items.find((i) => i.barcode === code || i.code === code);
    if (item) { add(item); toast.success(`${item.code} +1`); } else setUnknown(code);
  };
  return (
    <div className="space-y-3">
      <div className="grid gap-3 md:grid-cols-2">
        <F label="Leggi codice a barre (lettore o fotocamera)"><ScanInput placeholder="Punta il lettore qui e scansiona…" onCode={onCode} testId="receipt-barcode" title="Scansiona codice a barre" /></F>
        <F label="Oppure aggiungi a mano"><ItemPicker items={items} onPick={(i) => add(i)} testId="receipt-item-search" /></F>
      </div>
      {lines.length > 0 && (
        <div className="border bg-white" data-testid="receipt-lines">
          {lines.map((l, i) => (
            <div key={l.item.id} className="flex items-center gap-3 border-b px-3 py-2 last:border-b-0" data-testid={`receipt-line-${l.item.code}`}>
              <span className="flex-1 text-sm"><span className="font-mono text-xs font-semibold">{l.item.code}</span> · {l.item.name}{l.item.barcode && <span className="ml-2 font-mono text-[11px] text-muted-foreground">EAN {l.item.barcode}</span>}</span>
              <Input type="number" min="0" step="any" className="h-9 w-28 text-right font-mono" value={l.qty} onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)))} data-testid={`receipt-line-qty-${l.item.code}`} />
              <span className="w-10 text-xs text-muted-foreground">{l.item.unit}</span>
              <Button size="icon" variant="ghost" onClick={() => setLines(lines.filter((_, j) => j !== i))} data-testid={`receipt-line-remove-${l.item.code}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      )}
      <AssociateDialog code={unknown} items={items} onClose={() => setUnknown("")} onDone={(it) => { setItems((xs) => xs.map((x) => (x.id === it.id ? it : x))); add(it); setUnknown(""); }} />
    </div>
  );
};

const DeviceLines = ({ items, lines, setLines }) => {
  const [cur, setCur] = useState("");
  const item = items.find((i) => i.id === cur);
  const all = new Set(lines.flatMap((l) => l.serials.map((s) => `${l.item.id}|${s}`)));
  const addSerial = (s) => {
    if (!item) return toast.error("Scegli prima l'apparato");
    if (all.has(`${item.id}|${s}`)) return toast.error(`Seriale ${s} già in lista`);
    setLines((ls) => (ls.some((l) => l.item.id === item.id) ? ls.map((l) => (l.item.id === item.id ? { ...l, serials: [...l.serials, s] } : l)) : [...ls, { item, serials: [s] }]));
  };
  const drop = (id, s) => setLines((ls) => ls.map((l) => (l.item.id === id ? { ...l, serials: l.serials.filter((x) => x !== s) } : l)).filter((l) => l.serials.length));
  return (
    <div className="space-y-3">
      <div className="grid gap-3 md:grid-cols-2">
        <F label="Apparato">
          <Select value={cur || undefined} onValueChange={setCur}>
            <SelectTrigger className="h-11 bg-white" data-testid="receipt-device-item"><SelectValue placeholder="Seleziona tipo di apparato" /></SelectTrigger>
            <SelectContent>{items.map((i) => <SelectItem key={i.id} value={i.id}>{i.code} · {i.name}</SelectItem>)}</SelectContent>
          </Select>
        </F>
        <F label="Numero di serie (lettore, fotocamera o a mano + Invio)"><ScanInput placeholder={item ? "Scansiona o scrivi il seriale…" : "Scegli prima l'apparato"} onCode={addSerial} testId="receipt-serial" title="Scansiona seriale" /></F>
      </div>
      {lines.map((l) => (
        <div key={l.item.id} className="border bg-white p-3" data-testid={`receipt-device-line-${l.item.code}`}>
          <div className="mb-2 text-sm"><span className="font-mono text-xs font-semibold">{l.item.code}</span> · {l.item.name} — <b data-testid={`receipt-device-count-${l.item.code}`}>{l.serials.length}</b> pz</div>
          <div className="flex flex-wrap gap-1.5">{l.serials.map((s) => (
            <span key={s} className="inline-flex items-center gap-1 border bg-muted/40 px-2 py-0.5 font-mono text-xs" data-testid={`receipt-serial-chip-${s}`}>{s}
              <button type="button" onClick={() => drop(l.item.id, s)} aria-label="Rimuovi" data-testid={`receipt-serial-remove-${s}`}><Trash2 className="h-3 w-3" /></button></span>
          ))}</div>
        </div>
      ))}
    </div>
  );
};

const Recent = ({ rows }) => (
  <div className="border bg-white" data-testid="receipts-recent">
    <div className="micro-label border-b px-3 py-2">Ultimi carichi</div>
    {!rows.length ? <p className="px-3 py-3 text-sm text-muted-foreground">Nessun carico registrato.</p> : rows.map((r) => (
      <div key={r.id} className="flex flex-wrap justify-between gap-2 border-b px-3 py-2 text-sm last:border-b-0" data-testid={`receipt-row-${r.id}`}>
        <span><b>{r.kind === "DEVICE" ? "Apparati" : "Consumo"}</b> · {r.party_name}{r.doc_number && ` · DDT ${r.doc_number}`} → {r.warehouse_name}</span>
        <span className="text-xs text-muted-foreground">{r.lines.length} articoli · {r.total_qty} pz · {r.by} · {fmtDateTime(r.at)}</span>
      </div>
    ))}
  </div>
);

export const ReceiptTab = ({ items: allItems, warehouses, onChanged }) => {
  const { parties } = useData();
  const [kind, setKind] = useState("CONSUMABLE");
  const [items, setItems] = useState(allItems);
  const [head, setHead] = useState({ warehouse_id: "", supplier_id: "", customer_id: "", doc_number: "", doc_date: "", notes: "" });
  const [lines, setLines] = useState([]);
  const [recent, setRecent] = useState([]);
  const [busy, setBusy] = useState(false);
  const top = useRef(null);
  useEffect(() => { setItems(allItems); }, [allItems]);
  const loadRecent = () => api.get("/warehouse/receipts").then((r) => setRecent(r.data)).catch(() => {});
  useEffect(() => { loadRecent(); }, []);
  const device = kind === "DEVICE";
  const active = items.filter((i) => i.active !== false && !!i.serialized === device);
  const centrals = warehouses.filter((w) => w.kind === "CENTRAL" && w.active !== false);
  const listed = new Set(items.flatMap((i) => [i.supplier_id, ...(i.supplier_prices || []).map((p) => p.supplier_id)]).filter(Boolean));
  const partyList = parties.filter((p) => (device ? p.roles.includes("customer") : p.roles.includes("supplier") && listed.has(p.id)));
  const switchKind = (k) => { setKind(k); setLines([]); };
  const set = (k) => (v) => setHead((h) => ({ ...h, [k]: v }));
  const ready = head.warehouse_id && (device ? head.customer_id : head.supplier_id) && lines.length && (device || lines.every((l) => Number(l.qty) > 0));
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.post("/warehouse/receipts", { ...head, kind, doc_date: head.doc_date || null, supplier_id: device ? null : head.supplier_id, customer_id: device ? head.customer_id : null,
        lines: lines.map((l) => ({ item_id: l.item.id, qty: device ? 0 : Number(l.qty), serials: device ? l.serials : [] })) });
      toast.success(`Carico registrato: ${r.data.lines.length} articoli, ${r.data.total_qty} pezzi`);
      setLines([]);
      setHead((h) => ({ ...h, doc_number: "", doc_date: "", notes: "" }));
      loadRecent();
      onChanged();
      top.current?.scrollIntoView({ behavior: "smooth" });
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const total = device ? lines.reduce((s, l) => s + l.serials.length, 0) : lines.reduce((s, l) => s + Number(l.qty || 0), 0);
  return (
    <div className="space-y-4" ref={top} data-testid="receipt-tab">
      <div className="inline-flex border bg-white p-1" data-testid="receipt-kind">
        {[["CONSUMABLE", "Materiale di consumo (da fornitore)"], ["DEVICE", "Apparati elettronici (da committente)"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => switchKind(k)} className={`px-4 py-2 text-sm font-semibold transition-colors ${kind === k ? "bg-foreground text-background" : "hover:bg-muted"}`} data-testid={`receipt-kind-${k}`}>{l}</button>
        ))}
      </div>
      <div className="grid gap-3 border bg-white p-4 md:grid-cols-3">
        <F label="Magazzino centrale"><WarehouseSelect value={head.warehouse_id} onChange={set("warehouse_id")} warehouses={centrals} testId="receipt-warehouse" className="h-10 w-full bg-white" /></F>
        <F label={device ? "Committente che fornisce gli apparati" : "Fornitore"}>
          <Select key={kind} value={(device ? head.customer_id : head.supplier_id) || undefined} onValueChange={set(device ? "customer_id" : "supplier_id")}>
            <SelectTrigger className="h-10 bg-white" data-testid="receipt-party"><SelectValue placeholder={device ? "Seleziona committente" : "Seleziona fornitore"} /></SelectTrigger>
            <SelectContent>{partyList.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </F>
        <div className="grid grid-cols-2 gap-3">
          <F label="N. DDT"><Input className="h-10" value={head.doc_number} onChange={(e) => set("doc_number")(e.target.value)} data-testid="receipt-doc-number" /></F>
          <F label="Data DDT"><Input type="date" className="h-10" value={head.doc_date} onChange={(e) => set("doc_date")(e.target.value)} data-testid="receipt-doc-date" /></F>
        </div>
        {device && <p className="text-xs text-muted-foreground md:col-span-3">Gli apparati forniti dalla committente sono caricati a costo zero e potranno essere usati dai tecnici solo sugli ordini di quella committente.</p>}
      </div>
      {device ? <DeviceLines items={active} lines={lines} setLines={setLines} /> : <ConsumableLines items={active} lines={lines} setLines={setLines} setItems={setItems} />}
      <div className="flex flex-wrap items-center justify-end gap-3 border-t pt-3">
        <Input className="h-10 max-w-sm" placeholder="Note (facoltative)" value={head.notes} onChange={(e) => set("notes")(e.target.value)} data-testid="receipt-notes" />
        <span className="text-sm" data-testid="receipt-total">{lines.length} articoli · <b>{total}</b> pezzi</span>
        <Button disabled={!ready || busy} onClick={save} data-testid="receipt-save"><PackagePlus className="mr-2 h-4 w-4" />Conferma carico</Button>
      </div>
      <Recent rows={recent} />
    </div>
  );
};
